import { Layers } from '@modules/core';
import { getDeviceInfo } from '@modules/standard';
import { Icon } from '@zigbang/zuix2';
import { FormEvent, ReactNode, useCallback, useEffect, useState } from 'react';

import MousePrimaryDrag from './assets/tutorial/mouse/drag_primary.png';
import MouseSecondaryDrag from './assets/tutorial/mouse/drag_secondary.png';
import MouseWheelDown from './assets/tutorial/mouse/wheel_down.png';
import MouseWheelUp from './assets/tutorial/mouse/wheel_up.png';
import TouchDragDouble from './assets/tutorial/touch/drag_double.png';
import TouchDragSingle from './assets/tutorial/touch/drag_single.png';
import TouchPinchIn from './assets/tutorial/touch/pinch_in.png';
import TouchPinchOut from './assets/tutorial/touch/pinch_out.png';
import styles from './styles/TutorialModal.module.css';

export type TutorialItem = {
  image: ReactNode;
  title: ReactNode;
  description: ReactNode;
  className?: string;
  action?: () => void;
};

export const mouseTutorial: TutorialItem[] = [
  {
    image: MouseWheelUp,
    title: '확대 및 전방으로 이동',
    description: (
      <>
        마우스 휠 <em>앞으로</em> 드래그
      </>
    ),
  },
  {
    image: MouseWheelDown,
    title: '축소 및 후방으로 이동',
    description: (
      <>
        마우스 휠 <em>뒤로</em> 드래그
      </>
    ),
  },
  {
    image: MousePrimaryDrag,
    title: '화면 회전',
    description: (
      <>
        <em>(좌)클릭</em> 상태로 전방향 드래그
      </>
    ),
  },
  {
    image: MouseSecondaryDrag,
    title: '화면 이동',
    description: (
      <>
        <em>(우)클릭</em> 상태로 전방향 드래그
      </>
    ),
  },
];

export const touchTutorial: TutorialItem[] = [
  {
    image: TouchPinchOut,
    title: '전방으로 이동',
    description: '두 손가락으로 확대',
  },
  {
    image: TouchPinchIn,
    title: '후방으로 이동',
    description: '두 손가락으로 축소',
  },
  {
    image: TouchDragDouble,
    title: '화면 이동',
    description: '두 손가락으로 슬라이드',
  },
  {
    image: TouchDragSingle,
    title: '화면 회전',
    description: '한 손가락으로 슬라이드',
  },
];

export type TutorialSplashProps = {
  name?: string;
  items: TutorialItem[];
};

export function TutorialModal({ name = 'default', ...props }: TutorialSplashProps) {
  const [isVisible, setIsVisible] = useState(localStorage.getItem(`tutorial:${name}`) !== 'true');
  const [items] = useState([
    ...props.items,
    {
      image: null,
      title: null,
      description: null,
      className: styles.end,
      action: () => {
        setIsVisible(false);
      },
    },
  ]);
  const [carouselElement, setCarouselElement] = useState<HTMLElement | null>(null);
  const [carouselIndex, setCarouselIndex] = useState(0);

  const horizontalScroll = useCallback((event: WheelEvent) => {
    if (!(event.currentTarget instanceof Element)) return;
    const sign = Math.sign(event.deltaY);
    event.currentTarget.scrollBy({
      left: sign * Math.max(1, Math.min(100, Math.abs(event.deltaY) / 100)) * event.currentTarget.clientWidth * 0.25,
      behavior: 'smooth',
    });
  }, []);

  useEffect(
    function attachHorizontalScroll() {
      if (!carouselElement) return;

      carouselElement.addEventListener('wheel', horizontalScroll, { passive: false });

      return () => {
        carouselElement.removeEventListener('wheel', horizontalScroll);
      };
    },
    [carouselElement, horizontalScroll]
  );

  const getIndexFromScroll = useCallback(
    function getIndexFromScroll() {
      if (!carouselElement) return 0;
      return Math.round((carouselElement.scrollLeft / carouselElement.scrollWidth) * items.length);
    },
    [carouselElement, items.length]
  );

  useEffect(
    function onCarouselChange() {
      if (!carouselElement) return;

      let lastScroll = carouselElement.scrollLeft;
      const carouselIndexFromDirectionalScroll = () => {
        if (lastScroll === carouselElement.scrollLeft) return;
        const forward = lastScroll < carouselElement.scrollLeft;
        const index = getIndexFromScroll();
        if (forward && carouselIndex < index) setCarouselIndex(index);
        else if (!forward && index < carouselIndex) setCarouselIndex(index);
        lastScroll = carouselElement.scrollLeft;
      };

      carouselElement.addEventListener('scroll', carouselIndexFromDirectionalScroll);

      return () => {
        carouselElement.removeEventListener('scroll', carouselIndexFromDirectionalScroll);
      };
    },
    [carouselElement, carouselIndex, getIndexFromScroll]
  );

  const changeCarousel = useCallback(
    (event: FormEvent) => {
      if (!(event.target instanceof HTMLInputElement) || !carouselElement) return;
      const index =
        (carouselElement.childElementCount + parseInt(event.target.value)) % carouselElement.childElementCount;
      setCarouselIndex(index);
    },
    [carouselElement]
  );

  const prevCarousel = useCallback(() => {
    setCarouselIndex((carouselIndex - 1) % items.length);
  }, [carouselIndex]);

  const nextCarousel = useCallback(() => {
    setCarouselIndex((carouselIndex + 1) % items.length);
  }, [carouselIndex]);

  useEffect(
    function onCarouselIndex() {
      const element = carouselElement?.children[carouselIndex];
      if (!element || !(element instanceof HTMLElement)) return;

      items[carouselIndex]?.action?.();

      const index = getIndexFromScroll();
      if (index === carouselIndex) return;

      carouselElement.scrollTo({
        left: element.offsetLeft - carouselElement.offsetWidth / 2 + element.offsetWidth / 2,
        behavior: 'smooth',
      });
    },
    [carouselElement, carouselIndex, getIndexFromScroll]
  );

  useEffect(
    function saveView() {
      if (isVisible) return;

      localStorage.setItem(`tutorial:${name}`, 'true');
    },
    [name, isVisible]
  );

  return (
    <section
      className={[styles.fullscreen, styles.carouselWrapper].join(' ')}
      style={{
        ...(isVisible ? {} : { opacity: 0, visibility: 'hidden' }),
        zIndex: Layers.UI_CONTENTS,
      }}>
      <div className={styles.carousel} ref={setCarouselElement}>
        {items.map((item, index) => (
          <article key={index} className={item.className}>
            <h1>{item.title}</h1>
            <figure>
              {typeof item.image === 'string' ? <img src={item.image} alt={item.title?.toString()} /> : item.image}
            </figure>
            <p>{item.description}</p>
          </article>
        ))}
      </div>
      <nav className={styles.carouselNav}>
        <div
          className={styles.carouselPrev}
          style={
            carouselIndex > 0
              ? undefined
              : {
                  opacity: 0,
                  visibility: 'hidden',
                }
          }>
          <Icon shape="ArrowLeft" color="white" style={{ width: '100%', height: '100%' }} onPress={prevCarousel} />
        </div>
        <fieldset className={styles.carouselIndicator}>
          {items.map((item, index) => (
            <div className={item.className} key={index}>
              <input
                id={`carousel-${index}`}
                type="radio"
                name="carousel"
                value={index}
                checked={carouselIndex === index}
                onChange={changeCarousel}
              />
              <label htmlFor={`carousel-${index}`} />
            </div>
          ))}
        </fieldset>
        <div
          className={styles.carouselNext}
          style={
            carouselIndex < items.length - 1
              ? undefined
              : {
                  opacity: 0,
                  visibility: 'hidden',
                }
          }>
          <Icon shape="ArrowRight" color="white" style={{ width: '100%', height: '100%' }} onPress={nextCarousel} />
        </div>
      </nav>
    </section>
  );
}

export function UniversalTutorialModal() {
  const [isMobile] = useState(getDeviceInfo().isMobileOrTablet);
  return <TutorialModal name={isMobile ? 'touch' : 'mouse'} items={isMobile ? touchTutorial : mouseTutorial} />;
}

export default UniversalTutorialModal;
